import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Collapse,
  Grid2,
  IconButton,
  TextField,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  Refresh,
} from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  defaultPodRevenueAppliedFilters,
  podRevenueIgnoresDate,
  PodRevenueAppliedFilters,
} from "../../../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../../views/components/common/textFields/DatePickerRangeNew";
import { useDebounce } from "../../../../../../views/components/common/hooks/Debounce";
import { PodRevenueDashboardStore } from "../../../../stores/dashboards/PodRevenueDashboardStore";

const SEARCH_DEBOUNCE_MS = 400;

type DatePreset = "7d" | "30d" | "90d" | "6m" | "fy";

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "6m", label: "6m" },
  { id: "fy", label: "FY" },
];

const getIndianFyStart = (today: Dayjs): Dayjs => {
  const fyYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return dayjs(`${fyYear}-04-01`).startOf("day");
};

const getPresetRange = (preset: DatePreset): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().endOf("day");
  if (preset === "7d")
    return { start: dayjs().subtract(6, "day").startOf("day"), end };
  if (preset === "90d")
    return { start: dayjs().subtract(89, "day").startOf("day"), end };
  if (preset === "6m")
    return {
      start: dayjs().subtract(6, "month").add(1, "day").startOf("day"),
      end,
    };
  if (preset === "fy") return { start: getIndianFyStart(dayjs()), end };
  return { start: dayjs().subtract(29, "day").startOf("day"), end };
};

const matchDatePreset = (start: Dayjs, end: Dayjs): DatePreset | null => {
  for (const preset of DATE_PRESETS) {
    const range = getPresetRange(preset.id);
    if (start.isSame(range.start, "day") && end.isSame(range.end, "day"))
      return preset.id;
  }
  return null;
};

const filterValuesToIds = (items: FilterValue[]): number[] =>
  items
    .map((item) => Number(item.id))
    .filter((id) => Number.isInteger(id) && id > 0);

const mergeSelected = (
  list: FilterValue[],
  selected: FilterValue[],
): FilterValue[] => {
  const ids = new Set(list.map((item) => item.id));
  return [...selected.filter((item) => !ids.has(item.id)), ...list];
};

interface Props {
  store: PodRevenueDashboardStore;
}

/** Same filter model as RedemptionFilters: date range + Order ID + Customer (ids drop the date). */
const PodRevenueFilters: React.FC<Props> = observer(({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { orderStore, customerStore } = useStores();

  const [expanded, setExpanded] = useState(true);
  const [customerOpen, setCustomerOpen] = useState(false);
  const debouncedOrderQuery = useDebounce(store.orderQuery, SEARCH_DEBOUNCE_MS);
  const debouncedCustomerQuery = useDebounce(
    store.customerQuery,
    SEARCH_DEBOUNCE_MS,
  );

  const activePreset = matchDatePreset(store.draftFrom, store.draftTo);
  const dateIgnored =
    store.selectedOrderIds.length > 0 || store.selectedCustomers.length > 0;
  const orderOptions = useMemo(() => {
    const searched = store.orderQuery.trim()
      ? (orderStore.orderIds ?? []).map((id) => ({
          id: String(id),
          value: String(id),
        }))
      : [];
    return mergeSelected(searched, store.selectedOrderIds);
  }, [orderStore.orderIds, store.orderQuery, store.selectedOrderIds]);
  const customerOptions = useMemo(
    () =>
      mergeSelected(
        (customerStore.customers ?? [])
          .filter((customer) => customer.id != null)
          .map((customer) => ({
            id: String(customer.id),
            value: customer.name || String(customer.id),
          })),
        store.selectedCustomers,
      ),
    [customerStore.customers, store.selectedCustomers],
  );
  const displayedError = store.filterError || store.summaryError;

  useEffect(() => {
    void store.applyFilters(store.applied);
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrderIds = useCallback(
    async (text: string) => {
      const seq = store.beginOrderSearch();
      try {
        await orderStore.searchOrderIds(text);
        store.finishOrderSearch(seq);
      } catch {
        store.finishOrderSearch(seq, "Failed to search order IDs.");
      }
    },
    [orderStore, store],
  );

  const fetchCustomers = useCallback(
    async (text: string) => {
      try {
        await customerStore.searchCustomers({
          page_no: 1,
          limit: 20,
          search_text: text,
        });
      } catch {
        store.setFilterError("Failed to search customers.");
      }
    },
    [customerStore, store],
  );

  useEffect(() => {
    const text = debouncedOrderQuery.trim();
    if (!text) {
      store.cancelOrderSearch();
      return;
    }
    void fetchOrderIds(text);
  }, [debouncedOrderQuery, fetchOrderIds, store]);

  useEffect(() => {
    if (!customerOpen) return;
    void fetchCustomers(debouncedCustomerQuery.trim());
  }, [customerOpen, debouncedCustomerQuery, fetchCustomers]);

  const handleApply = () => {
    const applied: PodRevenueAppliedFilters = {
      from: store.draftFrom.startOf("day"),
      to: store.draftTo.endOf("day"),
      order_id: filterValuesToIds(store.selectedOrderIds),
      customer_id: filterValuesToIds(store.selectedCustomers),
      q: store.searchText.trim(),
    };
    if (
      !podRevenueIgnoresDate(applied) &&
      applied.from.isAfter(applied.to, "day")
    ) {
      store.setFilterError("Start date must be on or before the end date.");
      return;
    }
    store.setFilterError(null);
    void store.applyFilters(applied);
  };

  const handleClear = () => {
    store.clearDraft();
    void store.applyFilters(defaultPodRevenueAppliedFilters());
  };

  const busy =
    store.summaryLoading || store.productsLoading || store.linesLoading;
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        bgcolor: colors.softCardBackground,
        border: `1px solid ${colors.softCardBorder}`,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <CustomTypography
            variant="heading4"
            fontWeight={700}
            color={colors.navyText}
          >
            POD Revenue
          </CustomTypography>
          <CustomTypography
            variant="heading7"
            fontWeight={400}
            color={colors.mutedSlateText}
          >
            Revenue, orders, and products for POD orders.
          </CustomTypography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <AppButton
            label={busy ? "Refreshing..." : "Refresh"}
            variant="secondary"
            startIcon={<Refresh />}
            disabled={busy}
            onClick={() => void store.refresh()}
          />
          <Tooltip title={expanded ? "Hide filters" : "Show filters"}>
            <IconButton
              onClick={() => setExpanded((v) => !v)}
              size="small"
              aria-label="Toggle filters"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Grid2 container spacing={2} sx={{ mt: 1 }} columns={12}>
          <Grid2 size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: 3,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: "0 1 auto", minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CustomTypography
                    variant="heading8"
                    fontWeight={600}
                    color={colors.mutedSlateText}
                  >
                    Date
                  </CustomTypography>
                  <Tooltip
                    title={
                      dateIgnored
                        ? "Ignored while Order ID / Customer is set"
                        : "Filter by order date"
                    }
                  >
                    <InfoOutlined
                      sx={{ fontSize: 16, color: colors.mutedSlateText }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    opacity: dateIgnored ? 0.45 : 1,
                    pointerEvents: dateIgnored ? "none" : "auto",
                  }}
                >
                  {DATE_PRESETS.map((preset) => (
                    <AppButton
                      key={preset.id}
                      label={preset.label}
                      variant={
                        activePreset === preset.id ? "primary" : "grayscale"
                      }
                      onClick={() => {
                        const range = getPresetRange(preset.id);
                        store.setDraftFrom(range.start);
                        store.setDraftTo(range.end);
                      }}
                      customStyles={{
                        minHeight: 32,
                        height: 32,
                        minWidth: "auto",
                        px: 1.5,
                      }}
                    />
                  ))}
                  <Box>
                    <DateRangePickerNew
                      startDate={store.draftFrom}
                      endDate={store.draftTo}
                      onRangeSelect={(range) => {
                        if (range.startDate)
                          store.setDraftFrom(range.startDate.startOf("day"));
                        if (range.endDate)
                          store.setDraftTo(range.endDate.endOf("day"));
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <CustomTypography
                  variant="heading8"
                  fontWeight={600}
                  color={colors.mutedSlateText}
                >
                  Search
                </CustomTypography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by store name, cart ID, or product"
                  value={store.searchText}
                  onChange={(e) => store.setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleApply();
                  }}
                  sx={{
                    mt: 0.5,
                    "& .MuiInputBase-root": { height: 37 },
                    "& .MuiInputBase-input::placeholder": { fontSize: 13 },
                  }}
                />
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <AppSearchDropdownMulti
              title="Order ID"
              list={orderOptions}
              value={store.selectedOrderIds}
              local={false}
              floatingLabel
              loading={
                store.orderQuery !== debouncedOrderQuery || store.orderLoading
              }
              handleSearch={(text) => store.setOrderQuery(text ?? "")}
              handleSelect={(items) => store.setSelectedOrderIds(items)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <AppSearchDropdownMulti
              title="Customer"
              list={customerOptions}
              value={store.selectedCustomers}
              local={false}
              floatingLabel
              handleSearch={(text) => store.setCustomerQuery(text ?? "")}
              handleSelect={(items) => store.setSelectedCustomers(items)}
              onOpen={() => setCustomerOpen(true)}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
            sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}
          >
            <AppButton
              variant="transparentPrimary"
              label="Clear"
              onClick={handleClear}
              disabled={busy}
              customStyles={{ width: "100%" }}
            />
            <AppButton
              variant="primary"
              label="Apply"
              onClick={handleApply}
              disabled={busy}
              customStyles={{ width: "100%" }}
            />
          </Grid2>
        </Grid2>
      </Collapse>

      {displayedError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {displayedError}
        </Alert>
      ) : null}
    </Paper>
  );
});

export default PodRevenueFilters;
